// src/Product/AcceptInvite.js
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import "./AcceptInvite.css";

function isExpired(expiresAt) {
  if (!expiresAt) return false;
  const exp = new Date(expiresAt).getTime();
  return Number.isFinite(exp) && exp <= Date.now();
}

function normalizeToken(v) {
  return String(v || "").trim();
}

export default function AcceptInvite() {
  const navigate = useNavigate();
  const location = useLocation();

  const token = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return normalizeToken(query.get("token"));
  }, [location.search]);

  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Load invite via RPC (RLS-proof)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setError("");
      setInvite(null);

      if (!token) {
        setError("No invite token provided.");
        return;
      }

      try {
        const { data, error: rpcErr } = await supabase.rpc(
          "get_company_invite_by_token",
          { p_token: token }
        );

        if (rpcErr) throw rpcErr;

        const row = Array.isArray(data) ? data[0] : data;

        if (!row) {
          setError("Invite not found.");
          return;
        }

        if (row.accepted) {
          setError("This invite has already been accepted.");
          return;
        }

        if (isExpired(row.expires_at)) {
          setError("This invite has expired.");
          return;
        }

        if (!cancelled) setInvite(row);
      } catch (e) {
        console.error("Invite load error:", e);
        if (!cancelled) {
          setError(
            e?.message ||
              "Error loading invite. It may be invalid, expired, or restricted."
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!invite) {
      setError("Invite is not loaded yet.");
      return;
    }

    if (isExpired(invite.expires_at)) {
      setError("This invite has expired.");
      return;
    }

    if ((password || "").length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      // 1) Lock invite FIRST (RLS-proof)
      const { data: locked, error: lockErr } = await supabase.rpc(
        "lock_company_invite",
        { p_token: token }
      );

      if (lockErr) throw lockErr;
      if (!locked) {
        // Could be: already accepted OR expired OR token doesn't exist
        throw new Error(
          "Invite has already been used, expired, or is invalid."
        );
      }

      // 2) Sign up (new users only) — add token to user metadata
      const { data: signUpData, error: signUpError } =
        await supabase.auth.signUp({
          email: invite.invited_email,
          password,
          options: {
            data: {
              invite_token: token, // ✅ trigger uses this
            },
          },
        });

      if (signUpError) throw signUpError;

      /**
       * IMPORTANT:
       * - If email confirmation is ON, user may need to confirm email before login.
       * - Membership insert happens via DB trigger immediately on auth.users insert.
       */
      const createdUserId = signUpData?.user?.id;

      if (!createdUserId) {
        alert(
          "Account created. Please check your email to confirm, then log in."
        );
        navigate("/login");
        return;
      }

      alert("Invite accepted! You can now log in.");
      navigate("/login");
    } catch (err) {
      console.error("Accept invite error:", err);
      setError(err?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="accept-invite">
        <p className="error">{error}</p>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="accept-invite">
        <p>Loading invite…</p>
      </div>
    );
  }

  return (
    <div className="accept-invite">
      <h2>Accept Invite to {invite.company_name || "Company"}</h2>
      <p>
        You’re signing up as <strong>{invite.invited_email}</strong>
      </p>

      <form onSubmit={handleSubmit}>
        <div className="field">
          <label>New Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
          />
        </div>

        <div className="field">
          <label>Confirm Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
          />
        </div>

        <button type="submit" disabled={loading}>
          {loading ? "Processing…" : "Accept Invite"}
        </button>
      </form>
    </div>
  );
}
